/**
 * Light / dark selection.
 *
 * **Only the SIGNAL comes from Telegram. The palette stays ours.**
 * `themeParams` carries the user's Telegram colours, and adopting them would
 * make the app look like a different app on every client — which is the
 * opposite of the point of porting a palette. What Telegram is authoritative
 * about is which of the two the user is currently looking at.
 *
 * Precedence, highest first:
 *
 *   1. Telegram's `colorScheme`, inside a Mini App. It is the user's Telegram
 *      setting, and the webview's own `prefers-color-scheme` does not reliably
 *      follow it.
 *   2. The OS preference, in a plain browser.
 *
 * There is deliberately no manual toggle and nothing persisted. A toggle is a
 * setting to store, sync and get wrong; the OS already has one.
 *
 * HeroUI v3 selects dark on `.dark` OR `[data-theme="dark"]` — verified in
 * `@heroui/styles/dist/variants/index.css`, where its own `@custom-variant
 * dark` matches `:is(.dark, .dark *, [data-theme="dark"], [data-theme="dark"] *)`.
 * Either alone is sufficient. Both are set anyway: the class is what the
 * variant matches fastest, the attribute is what a human reads in devtools,
 * and writing one without the other invites a later "simplification" that
 * removes the wrong one.
 *
 * Setting an explicit value ALSO suppresses HeroUI's `prefers-color-scheme`
 * fallback branch, which is what makes the result deterministic rather than
 * "whatever the webview thinks".
 */

import { applyChromeColors, getColorScheme, onThemeChanged } from './webapp.ts';

export type ColorScheme = 'light' | 'dark';

function osPreference(): ColorScheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** What the app should render right now. */
export function resolveColorScheme(): ColorScheme {
  return getColorScheme() ?? osPreference();
}

function apply(scheme: ColorScheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', scheme === 'dark');
  root.dataset.theme = scheme;
  // Native form controls, scrollbars and the address bar.
  root.style.colorScheme = scheme;
  // Telegram's own chrome tracks the app, not the other way round.
  applyChromeColors(scheme);
}

/**
 * Apply the current scheme and keep it in step. Returns a teardown function.
 *
 * Both sources are watched: inside Telegram the `themeChanged` event fires and
 * the media query generally does not; in a browser it is the other way round.
 * Subscribing to both costs one listener and removes an "it only updates on
 * reload on Android" class of bug.
 */
export function startThemeSync(): () => void {
  apply(resolveColorScheme());

  const unsubscribeTelegram = onThemeChanged(() => apply(resolveColorScheme()));

  const media =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  const onMediaChange = () => apply(resolveColorScheme());
  media?.addEventListener('change', onMediaChange);

  return () => {
    unsubscribeTelegram();
    media?.removeEventListener('change', onMediaChange);
  };
}
